import { Outlet, useNavigate, Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { usePMSession } from "../hooks/usePMSession";
import { useLogout } from "../hooks/useLogout";
import { Sidebar } from "../components/Sidebar";
import { GlobeLocaleDropdown } from "../components/GlobeLocaleDropdown";
import { Button } from "../components/Button";

/**
 * Signed-in sidebar shell (D-46). Wraps every authenticated route inside
 * via React Router <Outlet>. Header strip + sidebar with 5 nav items.
 *
 * Plan 03-04 update: the Phase 2 Sidebar `onMapClick` indirection that
 * temporarily showed MapPlaceholder.tsx is removed — Map nav is now an
 * enabled route that navigates to / which renders MapScreen via Outlet.
 */
export function HomeShell() {
  const navigate = useNavigate();
  const { data: pm } = usePMSession();
  const logout = useLogout();

  const handleSignOut = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate("/login", { replace: true }),
    });
  };

  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr] grid-rows-[64px_1fr]" data-testid="home-shell">
      {/* Full-width header strip spanning both columns */}
      {/*
       * Phase 11 D-01 + D-02 + D-03 — perf-gated glassmorphism opt-in on
       * TopAppBar. Tailwind v4 `backdrop-blur-sm` resolves to 8 px (verified
       * via Context7 — RESEARCH § 1.5 + Pitfall 3); the arbitrary-value
       * escapes `[backdrop-filter:blur(8px)]` + `[-webkit-backdrop-filter:
       * blur(8px)]` are belt-and-braces to (a) pin the radius even if
       * Tailwind v4 config drifts and (b) emit the Safari `-webkit-` prefix
       * regardless of Tailwind's auto-vendor-prefixing config.
       *
       * **Surface alpha = 0.96 (NOT the plan-default 0.80).** The plan's
       * draft proposed `bg-surface-raised/80` so the blur reads obviously
       * through the surface. The Phase 9 chrome-axe-a11y probe revealed
       * that at alpha=0.80, the existing `text-text-secondary` ("Welcome
       * back, ${pm}") rendering on the blended surface fell to 3.55:1 —
       * BELOW the WCAG-AA 4.5:1 small-text floor. Static probe at impl-
       * time walked alpha 0.80→1.00 by 0.02 increments against the
       * effective composited backdrop axe-core sees in the test environment;
       * 0.95 gave 4.50:1 (borderline) and 0.96 gave 4.57:1 (margin of
       * safety). Picked 0.96 to align with the AssetSidePanel header
       * (Plan 11-05 Task 2 picked the same alpha for the same WCAG-AA
       * reason). Documented as Rule 1 auto-fix in 11-05-SUMMARY.md.
       *
       * The 4 % transparency vs Phase 9 baseline (`bg-surface-raised`,
       * alpha=1.0) is enough for the blur of the map cartography below
       * the TopAppBar to read through softly on the home/map shell while
       * preserving the accessibility floor.
       *
       * Cross-browser fallback (D-23): `web/src/styles/glassmorphism-fallback.
       * css` ships an `@supports not ((backdrop-filter: blur(8px)) or
       * (-webkit-backdrop-filter: blur(8px)))` block that overrides this
       * surface back to fully-opaque `bg-surface-raised + shadow-md` when
       * the browser doesn't support backdrop-filter (Firefox <103, IE).
       * Source-order specificity wins — no `!important` used.
       *
       * Reduced-motion (UI-SPEC line 692-694): blur is statically applied;
       * NO transition on backdrop-filter. `prefers-reduced-motion: reduce`
       * keeps the blur on (only animations are silenced — see
       * `web/src/index.css` reduced-motion positive-list selector). This is
       * deliberate: blur is a layout effect, not a motion effect.
       *
       * Wave 5 (Plan 11-06) measures p95 with blur enabled vs disabled;
       * if Galaxy A15 budget breaks, the verdict file flips this surface to
       * `fallback_solid` and a follow-up reverts to Phase 9 baseline. The
       * implementation here ASSUMES verdict=ship.
       */}
      <header
        className="relative z-30 col-span-2 h-16 bg-surface-raised/96 backdrop-blur-sm [backdrop-filter:blur(8px)] [-webkit-backdrop-filter:blur(8px)] border-be border-divider flex items-center gap-4 ps-6 pe-6"
        data-testid="app-header"
      >
        <span className="text-base font-semibold text-on-surface">
          <FormattedMessage id="app_name" />
        </span>
        <span className="flex-grow" />
        {pm && (
          <span className="text-sm text-text-secondary">
            <FormattedMessage id="LABEL_WELCOME_PM" values={{ username: pm.username }} />
          </span>
        )}
        <GlobeLocaleDropdown />
        <Link
          to="/account"
          className="inline-flex items-center gap-2 ps-3 pe-3 h-9 text-sm font-semibold text-on-surface hover:text-primary hover:bg-surface rounded-md focus-visible:ring-2 focus-visible:ring-primary transition-colors duration-200"
          data-testid="account-link"
        >
          <FormattedMessage id="LABEL_ACCOUNT_SECURITY" />
        </Link>
        <Button
          variant="secondary"
          onClick={handleSignOut}
          disabled={logout.isPending}
          data-testid="sign-out-button"
        >
          <FormattedMessage id="LABEL_SIGN_OUT" />
        </Button>
      </header>

      {/* Sidebar */}
      <Sidebar />

      {/* Main content area — MapScreen at /, Account at /account, etc.
          flex column so children that use h-full / flex-1 fill the grid
          track. MapView is full-bleed; Account / Workers apply their own
          padding inside their own root containers. */}
      <main className="bg-surface-raised flex flex-col min-h-0" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
